import { useCallback, useRef, useState } from "react";
import { FrondEngine } from "@frond/engine";
import { store } from "../state/store";

/**
 * Mounts the engine onto a <canvas> and wires it to the app store:
 * serialization (autosave), tool/selection/style state for the UI, and
 * document-change notifications.
 *
 * Uses a callback ref (not an effect) so the engine initializes the moment
 * the canvas mounts — the canvas only exists after `store.boot()` finishes
 * (the boot screen renders before that), and a plain `useEffect([], ...)`
 * would run too early, find no canvas, and never retry.
 */
export function useEngine() {
  const engineRef = useRef<FrondEngine | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [engine, setEngine] = useState<FrondEngine | null>(null);
  const [version, setVersion] = useState(0); // bump to re-render UI on engine events

  const attachRef = useCallback((canvas: HTMLCanvasElement | null) => {
    // Detach path (unmount or StrictMode re-attach): dispose the old engine.
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    engineRef.current?.dispose();
    engineRef.current = null;
    setEngine(null);

    if (!canvas) return;

    const engine = new FrondEngine({ canvas });
    engineRef.current = engine;
    setEngine(engine);

    store.engineSerializer = () => engine.serialize();
    store.engineLoader = (json: string, historyJson?: string | null) => engine.load(json, historyJson);
    store.engineHistorySerializer = () => engine.serializeHistory();
    store.engineHistoryLoader = (json: string) => engine.loadHistory(json);

    // Restore the last local session immediately (local-first).
    void store.restoreLocal(null).then((restored) => {
      if (!restored) engine.fitView();
    });

    const bump = () => setVersion((v) => v + 1);
    engine.on((e) => {
      if (e.type === "history" || e.type === "camera" || e.type === "tool" || e.type === "bookmarks") bump();
      if (e.type === "history") store.onDocumentChanged?.();
    });
    store.onDocumentChanged = () => store.markDirty();
    // Also mark dirty on stroke commits (history events) — throttled by store.
    const offHistory = engine.history.on(() => store.markDirty());

    cleanupRef.current = () => {
      offHistory();
      if (store.engineSerializer) store.engineSerializer = null;
      if (store.engineLoader) store.engineLoader = null;
      if (store.engineHistorySerializer) store.engineHistorySerializer = null;
      if (store.engineHistoryLoader) store.engineHistoryLoader = null;
      if (store.onDocumentChanged) store.onDocumentChanged = null;
      engine.dispose();
    };
  }, []);

  return { canvasRef: attachRef, engine, version };
}
